import { motion } from "framer-motion";
import { ThemedLogo } from "../../common/ui/themedLogo";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.08,
      duration: 0.45,
      ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number],
    },
  }),
};

const PILLARS = [
  {
    title: "Inteligencia de Datos",
    description:
      "Métricas en tiempo real, reportes históricos y análisis de tendencias para decisiones estratégicas.",
    iconPath:
      "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
  },
  {
    title: "Escalabilidad",
    description:
      "Arquitectura preparada para crecer con tu negocio, de un centro a una cadena completa.",
    iconPath:
      "M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01",
  },
  {
    title: "Experiencia UX",
    description:
      "Interfaz responsiva e intuitiva para gestionar clientes, pagos e inventario desde cualquier dispositivo.",
    iconPath:
      "M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z",
  },
  {
    title: "Seguridad",
    description:
      "Autenticación por roles, cifrado en tránsito, hashing de contraseñas y aislamiento multi-tenant bajo OWASP.",
    iconPath:
      "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
  },
];

const PillarIcon = ({ d }: { d: string }) => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d={d}
    />
  </svg>
);

export const AboutTab = () => {
  return (
    <motion.div
      className="flex flex-col gap-compact"
      initial="hidden"
      animate="visible"
    >
      {/* ── HERO ── */}
      <motion.div
        custom={0}
        variants={fadeUp}
        className="card-compact relative overflow-hidden"
      >
        {/* Ambient glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 60% 50% at 50% 0%, var(--color-accent-overlay) 0%, transparent 70%)",
            opacity: 0.5,
          }}
        />

        <div className="relative flex flex-col items-center text-center gap-5 py-8">
          <ThemedLogo className="h-16 w-auto" />

          <div className="flex flex-col gap-2 max-w-lg">
            <p
              className="text-body"
              style={{ lineHeight: "var(--leading-relaxed)" }}
            >
              Plataforma integral de gestión deportiva impulsada por análisis de
              datos en tiempo real para centros de fitness de alto rendimiento.
            </p>
          </div>

          {/* Decorative line */}
          <div className="flex items-center gap-3 w-full max-w-xs">
            <div className="h-px flex-1 bg-global-primary" />
            <div
              className="w-1.5 h-1.5 rounded-full"
              style={{ backgroundColor: "var(--btn-primary)" }}
            />
            <div className="h-px flex-1 bg-global-primary" />
          </div>
        </div>
      </motion.div>

      {/* ── PILLARS GRID ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-compact">
        {PILLARS.map((pillar, i) => (
          <motion.div
            key={pillar.title}
            custom={i + 1}
            variants={fadeUp}
            className="card-compact group flex flex-col gap-3 transition-colors duration-200"
            style={{ borderColor: "var(--border-primary)" }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor =
                "var(--btn-primary)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor =
                "var(--border-primary)";
            }}
          >
            {/* Icon + Title row */}
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                style={{
                  background: "var(--color-accent-overlay)",
                  color: "var(--text-accent)",
                }}
              >
                <PillarIcon d={pillar.iconPath} />
              </div>
              <h3
                className="text-size-sm font-semibold"
                style={{ color: "var(--text-title)" }}
              >
                {pillar.title}
              </h3>
            </div>

            <p
              className="text-caption"
              style={{ lineHeight: "var(--leading-relaxed)" }}
            >
              {pillar.description}
            </p>
          </motion.div>
        ))}
      </div>

      {/* ── VISION ── */}
      <motion.div custom={5} variants={fadeUp} className="card-compact">
        <div className="flex items-center gap-2.5 mb-3">
          <div
            className="w-1 h-4 rounded-full shrink-0"
            style={{ backgroundColor: "var(--btn-primary)" }}
          />
          <p className="text-label">Nuestra Visión</p>
        </div>
        <p
          className="text-body"
          style={{ lineHeight: "var(--leading-relaxed)" }}
        >
          DashCore AI nació para transformar la manera en que los centros
          deportivos gestionan sus operaciones diarias. Centralizamos clientes,
          planes, pagos, inventario, maquinaria y finanzas en un único panel,
          ofreciendo visibilidad completa del negocio con la claridad y
          velocidad que un equipo de gestión moderno necesita.
        </p>
      </motion.div>
    </motion.div>
  );
};
